import { Component, Input, OnChanges } from '@angular/core';
import { Role } from 'src/app/models/data/role';
import {
	SystemCapabilityEnum,
	SystemCapabilityItem,
} from 'src/app/models/data/systemCapability';
import { Mode } from 'src/app/models/model';
import { ApiService } from 'src/app/services/api.services';
import {
	BannerMessageService,
	BannerMessageType,
} from 'src/app/services/banner-message.service';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { SystemCapabilityTableComponent } from '../../components/system-capability-table/system-capability-table.component';
import { MatButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/list';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';

@Component({
	selector: 'app-role-detail-capabilities-table',
	templateUrl: './role-detail-capabilities-table.component.html',
	styleUrls: [
		'./role-detail-capabilities-table.component.scss',
		'../../../common/layout.scss',
		'../../../common/main.scss',
	],
	imports: [
		MatButton,
		MatDivider,
		MatFormField,
		MatLabel,
		MatInput,
		FormsModule,
		SystemCapabilityTableComponent,
	],
})
export class RoleDetailCapabilitiesTableComponent implements OnChanges {
	@Input() role!: Role;
	@Input() systemCapabilityItems: SystemCapabilityItem[] = [];
	@Input() companyId?: string;
	@Input() companyType?: 'advanced' | 'care';

	editRoleName = '';
	editItems: SystemCapabilityItem[] = [];
	originalScs: SystemCapabilityItem[] = [];
	canEditRoles: boolean;
	isEditing = false;

	get isViewOnly(): boolean {
		return !this.canEditRoles;
	}

	get editButtonDisabled(): boolean {
		return (
			this.role.isCompanyAdmin || this.role.isOracle || this.role.isSupport
		);
	}

	constructor(
		private readonly apiService: ApiService,
		private bannerMessageService: BannerMessageService,
		private readonly scService: SystemCapabilityService
	) {
		this.canEditRoles = this.scService.has(
			SystemCapabilityEnum.userRoleManagement
		);
	}

	ngOnChanges(): void {
		this.editRoleName = this.role.name ?? '';
		this.editItems = [...this.systemCapabilityItems];
		// copy away the original settings for cancel
		if (this.originalScs.length === 0) {
			this.originalScs = this.makeCopy(this.systemCapabilityItems);
		}
	}

	makeCopy(original: SystemCapabilityItem[]): SystemCapabilityItem[] {
		const scItemArray = [];
		for (const item of original) {
			const newItem = {
				systemCapability: {
					id: item.systemCapability.id,
					name: item.systemCapability.name,
					sentrilockOnly: item.systemCapability.sentrilockOnly,
				},
				granted: item.granted,
			};
			scItemArray.push(newItem);
		}
		return scItemArray;
	}

	resetValues(): void {
		this.editRoleName = this.role.name ?? '';
		this.editItems = this.makeCopy(this.originalScs);
	}

	onSystemCapabilityToggleEvent(item: SystemCapabilityItem): void {
		const editItem = this.editItems.find(
			(it) => it.systemCapability.name === item.systemCapability.name
		);
		if (editItem !== undefined) {
			editItem.granted = item.granted;
		}
	}

	mode(): Mode {
		return this.isEditing ? Mode.edit : Mode.viewOnly;
	}

	onCancelClicked(): void {
		this.isEditing = false;
		this.resetValues();
	}

	async onSaveEditsClicked(): Promise<void> {
		this.isEditing = false;
		let message = $localize`:@@roleDetailCapabilities-successMessage:Successfully saved role.`;
		let messageType = BannerMessageType.success;
		try {
			await this.apiService.updateRole(
				this.role.id,
				this.editRoleName,
				this.editItems
			);
			this.originalScs = this.makeCopy(this.editItems);
		} catch (e) {
			console.error(e); // better to print error than throw it
			message = $localize`:@@roleDetailCapabilities-failureMessage:Save failed.`;
			messageType = BannerMessageType.error;
			this.bannerMessageService.sendMessage(message, messageType);
			this.isEditing = true;
		}
		this.bannerMessageService.sendMessage(message, messageType);
	}

	isValid(): boolean {
		return this.editRoleName.trim().length > 0;
	}
}
